/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import clsx from 'clsx';
import * as actions from 'containers/Projects/actions';
import HorizontalMenu from 'components/Menus/HorizontalMenu';
import { saveWeddingsPreferences } from 'apollo/projects.query';
import ProjectModal from 'components/Modal/Project';
import APModal from 'components/Modal/APModal';
import { LandingContent } from 'components/LandingContent';
import upgradePlanImage from 'assets/images/account-plan-upgrade.jpeg';
import Search from 'components/Search';
import UpdateAddress from 'components/UpdateAddress/UpdateAddress';
import { authAccountWithIntegration } from 'utils/integrations';
import { setCookie } from 'utils/cookies';
import Projects from './Projects';
import ProjectsViewOptions from './Projects.ViewOption';
import { SET_PROJECTS_VIEW_OPTIONS } from '../../containers/App/constants';
import { ProjectsActionButton } from './Projects.ActionButton';
import { pollLoadingProjects } from './utils/refreshProjects';
import './styles.scss';

const ProjectsIndex = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.global.userInfo);
  const history = useHistory();
  const location = useLocation();
  const projectsViewOptions = useSelector((state) => state.global.projectsViewOptions);
  const projectsLimit = useSelector((state) => state.projects.projectsLimit);
  const [isActive, setisActive] = useState(true);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [reauthModalOpen, setOpenReauthModal] = useState(false);

  const {
    getProjectLimits,
  } = bindActionCreators(actions, dispatch);

  const currentProjects = isActive ? useSelector((state) => state.projects.activeProjects)
  : useSelector((state) => state.projects.archivedProjects);
  const showLanding = currentProjects.total < 1;

  const onClickActive = ({ item, key, keyPath, domEvent }) => {
    setSearchValue('');
    setisActive(true);
    activeRef.current.updateList(0, searchKey, projectsViewOptions.sort, true);
  };

  const onClickArchive = ({ item, key, keyPath, domEvent }) => {
    setSearchValue('');
    setisActive(false);
    activeRef.current.updateList(0, searchKey, projectsViewOptions.sortArchive || projectsViewOptions.sort, false);
  };

  const [searchKey, setSearchValue] = useState('');
  const activeRef = useRef();

  const activeProjects = useSelector((state) => state.projects.activeProjects.projects);
  const archivedProjects = useSelector((state) => state.projects.archivedProjects.projects);
  const showProjectTabs = activeProjects?.length > 0 || archivedProjects?.length > 0;
  const hasProTools = userInfo?.features?.includes('PRO-PLANNING');
  const isBasicAccount = !hasProTools;

  // PERM: Only subscribed users (trial, promo, and paying) and non-bookkeepers can see
  // the archive tab.
  const showArchiveTab = hasProTools;

  // Check for any projects still processing or in error.
  const hasProcessingProjects = activeProjects.some(p => p.creationStatus === 'processing');
  const [pollTimer, setPollTimer] = useState();

  useEffect(() => {
    if (hasProcessingProjects) {
      // Keep fetching projects until there are no more processing.
      setPollTimer(pollLoadingProjects(dispatch));
    } else if (pollTimer) {
      clearInterval(pollTimer);
    }
  }, [hasProcessingProjects]);

  const [openProjectsLimitErrorModal, setOpenProjectsLimitErrorModal] = useState(false);

  const placeholder = useMemo(() => {
    if (isActive) {
      return 'Search Active Projects';
    }
    return 'Search Archive';
  }, [isActive]);

  const saveWeddingsPreferencesCommand = async (options) => {
    const { filter = 'show-all' } = options;
    const data = await saveWeddingsPreferences(options);
    const { preferences } = data.saveWeddingsPreferences;
    // Need to set leadId to 0 if it's null
    // and also set filter to "show-all" if null.
    dispatch({
      type: SET_PROJECTS_VIEW_OPTIONS,
      payload: {
        ...preferences,
        filter,
      },
    });
  };

  const handleChangeOptions = (e) => {
    const { name, value } = e.target;
    projectsViewOptions[name] = value;

    if (isActive && value === 'show-all') {
      projectsViewOptions.leadId = 0;
    }

    if (isActive) {
      if (value === 'show-all') {
        activeRef.current.updateList(0, searchKey, projectsViewOptions.sort);
        projectsViewOptions.leadId = 0;
        saveWeddingsPreferencesCommand(projectsViewOptions);
      } else if (name === 'leadId') {
        projectsViewOptions.filter = 'lead-name';
        projectsViewOptions.leadId = value;
        saveWeddingsPreferencesCommand(projectsViewOptions);
      } else {
        saveWeddingsPreferencesCommand(projectsViewOptions);
      }
    } else {
      saveWeddingsPreferencesCommand(projectsViewOptions);
    }
  };

  const handleChangeCheckbox = (e) => {
    const { id, checked } = e.target;
    projectsViewOptions[id] = checked;

    saveWeddingsPreferencesCommand(projectsViewOptions);
  };

  const afterDismissViewOptions = () =>{
    activeRef.current.updateList(projectsViewOptions.leadId, searchKey, projectsViewOptions.sort);
  };

  // Create project modal
  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [openUpgradePlanModal, setOpenUpgradePlanModal] = useState(false);

  const showProjectModal = () => {
    if (isBasicAccount) {
      setOpenUpgradePlanModal(true);
    } else if (projectsLimit.isExceeded) {
      setOpenProjectsLimitErrorModal(true);
    } else {
      setOpenProjectModal(true);
    }
  };

  const items = [
    {
      id: 'active',
      name: 'Active Projects',
      onClick: onClickActive,
      active: isActive,
    },
    {
      id: 'archive',
      name: 'Archive',
      onClick: onClickArchive,
      active: !isActive,
      show: showArchiveTab,
    },
  ];

  const upgradePlanContent = () => (
    <>
      Designed by event pros, for event pros, Aisle Planner’s sales
      and project management tools are linked seamlessly together in one
      stylish, intuitive, and easy-to-use online environment. Build your
      processes and workflows, manage every document and detail, and collaborate
      with your team, your clients, and your vendor partners all in one place,
      all behind one login. Upgrade now and put the “Power of One” to work for
      your business today! Plans start at just $44.99/month. Learn more about
      all Aisle Planner has to offer{' '}
      <a
        target="_blank"
        rel="noreferrer"
        href="https://www.aisleplanner.com/solutions/tools-for-event-professionals"
      >
        here
      </a>{' '}
      .
    </>
  );

  // Update project limits when modal is opened.
  useEffect(() => {
    getProjectLimits();
  }, [openProjectModal]);

  // Inital page load.
  useEffect(() => {
    getProjectLimits();
    const sortArchive = projectsViewOptions?.sortArchive;
    const sort = projectsViewOptions?.sort;
    dispatch({
      type: SET_PROJECTS_VIEW_OPTIONS,
      payload: {
        ...projectsViewOptions,
        sort: projectsViewOptions.sort,
        sortArchive:  sortArchive || sort,
      },
    });
  }, [isActive]);

  const projectsLimitErrorOnClose = () => {
    if (projectsLimit?.isOwner) {
      history.push('/app/v2/account/billing');
    } else {
      setOpenProjectsLimitErrorModal(false);
    }
  };

  const fetchProjects = () => activeRef.current.updateList(0, searchKey, projectsViewOptions.sort, true);

  const projectsLimitErrorAction = () => {
    return (
      <button
        className="button"
        onClick={projectsLimitErrorOnClose}
      >
        {projectsLimit?.isOwner ? 'Upgrade subscription' : 'Got it'}
      </button>
    );
  };

  const projectsLimitErrorContent = () => {
    if (projectsLimit?.isOwner) {
      return (`Your current subscription limits you to ${projectsLimit.limit} projects.
                To create additional projects, upgrade your plan.`);
    }
    return (`To create additional projects,
                please contact the owner of your account to upgrade your plan.`);
  };

  const expiredIntegration = userInfo.integrations?.find(integration => integration?.grantStatus === 'expired');

  useEffect(() => {
    if (expiredIntegration) {
      setOpenReauthModal(true);
    }
  }, [expiredIntegration]);

  const reauthenticateAccountAction = (integration) => {
    return (
      <button
        className="button"
        onClick={() => handleReauthenticateAccount(integration)}
      >
        Reauthenticate Account
      </button>
    );
  };

  const handleReauthenticateAccount = async (integration) => {
    const provider = integration?.provider;

    if (!provider) {
      setErrorModal('No provider found for reauthentication.');
      return;
    }

    setCookie('nylas_exchange_redirect_url', location.pathname, 10 / 1440);
    await authAccountWithIntegration({ provider }, setErrorModal);
  };

  const setErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  return (
    <div
      className={clsx(
        'content-container',
        showLanding && 'content-container--narrow',
        'index-page',
        'project-index',
      )}
    >
      <div className="page-title page-title--actions">
        <h1 className="title">Projects</h1>
        {activeProjects?.length > 0 &&(
          <Search
            placeholderText={placeholder}
            fetchTableData={fetchProjects}
            searchTableData={fetchProjects}
            searchKey={searchKey}
            setSearchValue={setSearchValue}
          />
        )}
        <div className={clsx('page-title__actions')}>
          <ProjectsActionButton showProjectModal={showProjectModal} />
        </div>
      </div>
      {showProjectTabs && <HorizontalMenu items={items} />}
      <div className="index-page__top project-index__top">
        {/* eslint-disable */}
        {!isActive && (
          <p className="index-page__intro project-index__intro">
            Archived projects do not count towards your plan limit and are
            always available here in read-only mode, so you can always reference
            the important information that you need from past projects and
            events. Learn more about archived projects{' '}
            <a
              href="https://help.aisleplanner.com/en/articles/2126318-archiving-a-past-event-or-project"
              target="_blank"
            >
              here
            </a>
            .
          </p>
        )}
        {/* eslint-enable */}
        {projectsViewOptions && showProjectTabs && (
          <ProjectsViewOptions
            options={projectsViewOptions}
            handleChangeOptions={handleChangeOptions}
            handleChangeCheckbox={handleChangeCheckbox}
            afterDismissViewOptions={afterDismissViewOptions}
            viewingArchive={!isActive}
          />
        )}
      </div>
      <Projects
        options={projectsViewOptions}
        searchKey={searchKey}
        showProjectModal={showProjectModal}
        isActive={isActive}
        ref={activeRef}
      />
      <ProjectModal
        open={openProjectModal}
        onClose={() => setOpenProjectModal(false)}
      />
      <APModal
        open={openUpgradePlanModal}
        onClose={() => setOpenUpgradePlanModal(false)}
        name="upgrade-plan"
      >
        <div className="content-container content-container--narrow">
          <LandingContent
            action={() => {
              history.push('/app/v2/account/billing');
            }}
            title="Do more with Aisle Planner"
            description={upgradePlanContent()}
            actionButtonTitle="Upgrade now"
            heroImage={upgradePlanImage}
          />
        </div>
      </APModal>
      <APModal
        open={openProjectsLimitErrorModal}
        onClose={() => setOpenProjectsLimitErrorModal(false)}
        name="projects-limit-error"
        title="You've reached your project limit."
        actions={projectsLimitErrorAction()}
      >
        <div className="content-container content-container--narrow">
          <div>{projectsLimitErrorContent()}</div>
        </div>
      </APModal>

      <APModal
        open={reauthModalOpen}
        onClose={() => setOpenReauthModal(false)}
        title="Heads up!"
        actions={reauthenticateAccountAction(expiredIntegration)}
      >
        <p>
          For security purposes, your Google/Apple/Outlook account needs to be reauthenticated in order to continue
          syncing with Aisle Planner. Please click below to be redirected to the authentication page.
        </p>
      </APModal>

      <APModal
        open={errorModalOpen}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>

      <UpdateAddress />
    </div >
  );
};

export default ProjectsIndex;
